import { memo, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DragDropContext, Draggable, Droppable, type OnDragEndResponder } from '@hello-pangea/dnd'
import { Button, PlusIcon, showToast } from '@quickmd-llc/design-system'
import { type ServiceTypeListItemAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { toggleCancelPressed } from '@/store/newService/NewServiceActions'
import { useServiceTypes } from '@/hooks/useServiceTypes'
import { CREATE_NEW_SERVICE_TYPE_ROUTE } from '@/utils/constants'
import { ServiceTypeRow } from './components/ServiceTypeRow/ServiceTypeRow'
import { useReorderServiceTypes } from './hooks/useReorderServiceTypes'

const ServiceTypes = memo(() => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { serviceTypes } = useServiceTypes()
  const { reorder } = useReorderServiceTypes()
  const isCanceledPressed = useAppSelector((state) => state.newService.cancelPressed)
  const [characters, updateCharacters] = useState<ServiceTypeListItemAdminApiOutContract[]>(serviceTypes)

  const onDragEndHandler = useCallback<OnDragEndResponder>(
    (result) => {
      const table = document.getElementById('table')
      table?.scrollTo(0, window.scrollY)
      if (!result.destination) return
      const previousOrder = characters
      const items = [...characters]
      const spliced = items.splice(result.source.index, 1)
      const reorderItem = spliced[0]
      if (reorderItem === undefined) return
      items.splice(result.destination.index, 0, reorderItem)
      updateCharacters(items)
      reorder(
        items.map((t) => t.ServiceTypeId ?? ''),
        {
          onError: (message) => {
            // The PUT was rejected — revert to the last server-accepted order and surface why,
            // rather than leaving the list showing an order that never persisted.
            updateCharacters(previousOrder)
            showToast({ type: 'error', message, position: 'bottom-right' })
          },
        },
      )
    },
    [characters, reorder],
  )

  useEffect(() => {
    if (isCanceledPressed) {
      dispatch(toggleCancelPressed(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to clear a stale cancel-pressed flag; deps intentionally omitted to avoid re-running on every dispatch
  }, [])

  useEffect(() => {
    updateCharacters(serviceTypes)
  }, [serviceTypes])

  const onClickNewService = useCallback(() => {
    void navigate(CREATE_NEW_SERVICE_TYPE_ROUTE, { state: { search: location.search } })
  }, [navigate, location.search])

  return (
    <div className='flex flex-auto flex-col'>
      <div className='flex items-center justify-between rounded-t-large border border-b-0 border-shade-10 bg-white px-[28px] py-[24px]'>
        <div className='flex flex-col gap-[4px]'>
          <span className='text-xl font-semibold leading-7 text-text-dark'>Configure services</span>
          <span className='text-base font-normal leading-5 tracking-[0.12px] text-text-light'>
            Drag to set the order of how service types appear in the app.
          </span>
        </div>
        <Button priority='secondary' size='m' onClick={onClickNewService}>
          <PlusIcon iconSize='m' />
          New service type
        </Button>
      </div>
      <DragDropContext onDragEnd={onDragEndHandler}>
        <Droppable droppableId='droppableServiceTypes'>
          {(provided, snapshot) => (
            <div
              id='table'
              className={`flex min-h-[80px] flex-col overflow-hidden rounded-b-large border border-shade-10 ${
                snapshot.isDraggingOver ? 'bg-[#f2fafb]' /* surface-teal-soft — no exact DS token */ : 'bg-white'
              }`}
              ref={provided.innerRef}
              {...provided.droppableProps}>
              {characters.length === 0 ?
                <ServiceTypeRow type='empty' />
              : characters.map((t, i) => (
                  <Draggable key={t.ServiceTypeId ?? i} draggableId={t.ServiceTypeId ?? `${i}`} index={i}>
                    {(dragProvided, dragSnapshot) => (
                      <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <ServiceTypeRow
                          t={t}
                          dragHandleProps={dragProvided.dragHandleProps}
                          isDragging={dragSnapshot.isDragging}
                          isLast={i === characters.length - 1}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              }
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
})

ServiceTypes.displayName = 'ServiceTypes'

export default ServiceTypes
